// ABOUTME: Full-screen job detail: job facts (delete) and attempts (retry, cancel; the selected one
// ABOUTME: opened) on the left; the attempt's result, logs or drill-down on the right; close below.

import {
    ArrowBackIcon,
    Box,
    Button,
    CancelOutlinedIcon,
    CircularProgress,
    DeleteOutlineIcon,
    Dialog,
    ReplayIcon,
    SectionHeader,
    Stack,
    Typography,
} from '@ds/coolshop';
import { ConfirmDialogResponse } from '@src/components/shared/ConfirmDialog';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAttemptLogs } from '../../hooks/useAttemptLogs/useAttemptLogs';
import { useJobActions } from '../../hooks/useJobActions/useJobActions';
import { useJobActor } from '../../hooks/useJobActor/useJobActor';
import { useJobDetail } from '../../hooks/useJobDetail/useJobDetail';
import { useJobTypes } from '../../hooks/useJobTypes/useJobTypes';
import { useJobTypeLabel } from '../../registry';
import { DetailType, JobAttempt, LIVE_ATTEMPT_STATES, LogLevel } from '../../types';
import { canCancelJob, canDeleteJob, canRetryJob } from '../../utils/jobPermissions';
import { ActionIcon } from '../ActionIcon';
import { JobActionConfirmDialog, JobConfirmAction } from '../JobActionConfirmDialog';
import { AttemptList } from './Attempt/AttemptList';
import { AttemptLogs } from './Attempt/AttemptLogs';
import { argumentKeys, JobArguments } from './Attempt/JobArguments';
import { JobSummary } from './Attempt/JobSummary';
import { toResultView } from './Attempt/toResultView';
import { JobDetails } from './JobDetails/JobDetails';
import { JobOverview } from './JobOverview/JobOverview';

interface IJobDialogProps {
    jobId: string;
    onClose: () => void;
    /** Called after a retry, cancel or delete made from the dialog, to refresh the list. */
    onChanged?: () => void;
}

const ISSUE_LEVELS: LogLevel[] = ['warn', 'error'];

/** The height the attempts keep before the side column itself starts scrolling. */
const ATTEMPTS_MIN_HEIGHT = 260;

export function JobDetailDialog(props: Readonly<IJobDialogProps>) {
    const { onClose, onChanged, jobId } = props;
    const intl = useIntl();
    const [open, setOpen] = useState(true);
    const [detailType, setDetailType] = useState<DetailType>(DetailType.OVERVIEW);
    // null follows the job's current attempt, so a retry shows up as soon as it starts.
    const [selectedAttempt, setSelectedAttempt] = useState<number | null>(null);
    const [confirm, setConfirm] = useState<JobConfirmAction | null>(null);
    const { job, error, refresh } = useJobDetail(jobId);
    const { byType } = useJobTypes();
    const actor = useJobActor();
    const jobTypeLabel = useJobTypeLabel();

    const attempts = useMemo<JobAttempt[]>(
        () => [...(job?.attempts ?? [])].sort((a, b) => a.n - b.n),
        [job?.attempts],
    );
    const attempt =
        attempts.find(candidate => candidate.n === (selectedAttempt ?? job?.currentAttempt)) ??
        attempts[attempts.length - 1];
    const live = !!attempt && LIVE_ATTEMPT_STATES.includes(attempt.state);

    // The warning and error lines feed the overview counters and their drill-down tables.
    const { entries: issues } = useAttemptLogs(job?.id, attempt?.n, ISSUE_LEVELS, live);
    const resultView = useMemo(
        () => (job ? toResultView(job, attempt, issues) : null),
        [job, attempt, issues],
    );

    const handleBack = () => setDetailType(DetailType.OVERVIEW);

    const handleClose = () => {
        setOpen(false);
        onClose();
    };

    const { retry, cancel, remove, pendingId } = useJobActions(action => {
        onChanged?.();
        // A deleted job is gone: the dialog closes instead of reloading it.
        if (action !== 'delete') refresh();
    });
    const busy = !!job && pendingId === job.id;

    const handleRetry = async () => {
        if (!job) return;
        if (await retry(job)) {
            // Follow the new attempt, whose tab appears with the refreshed job.
            setSelectedAttempt(null);
            setDetailType(DetailType.OVERVIEW);
        }
    };

    const handleConfirm = async (response: ConfirmDialogResponse) => {
        const action = confirm;
        setConfirm(null);
        if (response !== ConfirmDialogResponse.OK || !action || !job) return;
        if (action === 'retry') {
            await handleRetry();
        } else if (action === 'cancel') {
            await cancel(job);
        } else if (await remove(job)) {
            handleClose();
        }
    };

    // Same icons as the actions column of the job list: the job itself can be deleted, its
    // execution retried or cancelled.
    const jobActions = job
        ? [
              {
                  key: 'delete',
                  label: intl.formatMessage({ id: 'job.action.delete', defaultMessage: 'Delete' }),
                  icon: <DeleteOutlineIcon fontSize="small" />,
                  visible: canDeleteJob(job, actor),
                  onClick: () => setConfirm('delete'),
              },
          ].filter(action => action.visible)
        : [];
    const attemptActions = job
        ? [
              {
                  key: 'retry',
                  label: intl.formatMessage({ id: 'job.action.retry', defaultMessage: 'Retry' }),
                  icon: <ReplayIcon fontSize="small" />,
                  visible: canRetryJob(job, byType),
                  onClick: () => setConfirm('retry'),
              },
              {
                  key: 'cancel',
                  label: intl.formatMessage({ id: 'job.action.cancel', defaultMessage: 'Cancel' }),
                  icon: <CancelOutlinedIcon fontSize="small" />,
                  visible: canCancelJob(job, actor),
                  onClick: () => setConfirm('cancel'),
              },
          ].filter(action => action.visible)
        : [];

    const renderActions = (actions: typeof jobActions) => (
        <Box display="flex" alignItems="center" gap={0.5}>
            {actions.map(action => (
                <ActionIcon
                    key={action.key}
                    label={action.label}
                    icon={action.icon}
                    disabled={busy}
                    onClick={action.onClick}
                />
            ))}
        </Box>
    );

    // With many attempts only their list scrolls, the job facts above it staying in view; when the
    // window is too short even for a few attempts (ATTEMPTS_MIN_HEIGHT), the whole column scrolls.
    const sidebar = job && (
        <Stack gap={3} flex={1} minHeight={0}>
            <Stack gap={1} flexShrink={0}>
                <SectionHeader
                    label={intl.formatMessage({ id: 'job.detail.job', defaultMessage: 'Job' })}
                    endAdornment={renderActions(jobActions)}
                />
                <JobSummary job={job} />
            </Stack>
            {argumentKeys(job.args ?? {}, byType[job.type]?.argsSchema, actor.isSuperAdmin).length >
                0 && (
                <Stack gap={1} flexShrink={0}>
                    <SectionHeader
                        label={intl.formatMessage({
                            id: 'job.detail.arguments',
                            defaultMessage: 'Arguments',
                        })}
                    />
                    <JobArguments
                        args={job.args}
                        schema={byType[job.type]?.argsSchema}
                        showDerived={actor.isSuperAdmin}
                    />
                </Stack>
            )}
            <Stack gap={1} minHeight={ATTEMPTS_MIN_HEIGHT}>
                <SectionHeader
                    label={intl.formatMessage({
                        id: 'job.detail.attempts',
                        defaultMessage: 'Attempts',
                    })}
                    endAdornment={renderActions(attemptActions)}
                />
                {attempts.length > 0 ? (
                    <AttemptList
                        attempts={attempts}
                        selected={attempt?.n}
                        onSelect={n => {
                            setSelectedAttempt(n);
                            setDetailType(DetailType.OVERVIEW);
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage({
                            id: 'job.attempt.none',
                            defaultMessage: 'This job has not run yet.',
                        })}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );

    const renderContent = () => {
        if (!job || !resultView) {
            return (
                <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                    {error ? <Typography color="error">{error}</Typography> : <CircularProgress />}
                </Box>
            );
        }
        if (detailType !== DetailType.OVERVIEW) {
            return (
                <Stack gap={1} flex={1} minHeight={0}>
                    <Box>
                        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                            {intl.formatMessage({ id: 'job.detail.back', defaultMessage: 'Back' })}
                        </Button>
                    </Box>
                    <JobDetails job={resultView} detailType={detailType} />
                </Stack>
            );
        }
        return (
            <JobOverview
                job={resultView}
                handleToggleDetails={setDetailType}
                afterSections={
                    attempt && <AttemptLogs jobId={job.id} attempt={attempt.n} live={live} />
                }
            />
        );
    };

    const footer = (
        <Box
            display="flex"
            justifyContent="flex-end"
            px={4}
            py={2}
            borderTop="1px solid"
            borderColor="divider"
        >
            <Button variant="contained" size="large" onClick={handleClose}>
                {intl.formatMessage({ id: 'job.detail.close', defaultMessage: 'Close' })}
            </Button>
        </Box>
    );

    return (
        <>
            <Dialog
                onClose={handleClose}
                open={open}
                fullScreen
                header={{
                    title: job ? jobTypeLabel(job.type) : '',
                    onClose: handleClose,
                }}
            >
                <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
                    <Box display="flex" flex={1} minHeight={0}>
                        <Box
                            width={360}
                            flexShrink={0}
                            px={3}
                            pt={0}
                            pb={3}
                            display="flex"
                            flexDirection="column"
                            overflowY="auto"
                        >
                            {sidebar}
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            flex={1}
                            minWidth={0}
                            px={4}
                            pt={1}
                            pb={3}
                            overflowY="auto"
                        >
                            {renderContent()}
                        </Box>
                    </Box>
                    {footer}
                </Box>
            </Dialog>
            <JobActionConfirmDialog
                action={confirm}
                jobLabel={job ? jobTypeLabel(job.type) : ''}
                handleClose={handleConfirm}
            />
        </>
    );
}
